import { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Select,
    MenuItem,
    Typography,
    Button,
    FormControl,
    InputLabel,
    FormHelperText,
    Divider,
} from "@mui/material";
import { toLonLat } from "ol/proj";
import { getArea, getLength } from 'ol/sphere.js';

/**
 * FeatureAttributeForm
 *
 * Schema-driven attribute form for a drawn OL feature.
 *
 * Field rules:
 *   auto: true          → read-only, resolved from geometry
 *   options: [...]      → Select dropdown
 *   data_type: float/int → number input
 *   data_type: string   → text input
 *
 * @param {Object}   feature   - OL Feature just drawn
 * @param {Array}    schema    - Array of field descriptors
 * @param {Function} onSubmit  - Called with { key: value } plain object
 * @param {Function} onCancel  - Called if user cancels
 */
export default function FeatureAttributeForm({ feature, schema, onSubmit, onCancel, required_all = false }) {
    const [values, setValues] = useState({});
    const [errors, setErrors] = useState({});

    // ── Initialise values from schema ──
    useEffect(() => {
        if (!feature || !schema) return;

        const initial = {};
        schema.forEach(field => {
            if (field.auto && field.default) {
                initial[field.key] = resolveAuto(field.default, feature);
            } else {
                initial[field.key] = "";
            }
        });

        setValues(initial);
        setErrors({});
    }, [feature, schema]);

    // ── Resolve auto expressions against the feature geometry ──
    const resolveAuto = (expression, feat) => {
        console.log(expression, feat);
        try {
            const extent = feat.getGeometry().getExtent();
            const lon = (extent[0] + extent[2]) / 2;
            const lat = (extent[1] + extent[3]) / 2;
            if (expression === "geom.centroid.y") return parseFloat(lat.toFixed(6));
            if (expression === "geom.centroid.x") return parseFloat(lon.toFixed(6));
            if (expression === "geom.length_km") {
                // getLength returns meters, projection defaults to EPSG:4326
                const metres = getLength(feat.getGeometry(), { projection: "EPSG:4326" });
                
                return parseFloat((metres / 1000).toFixed(6));
            }
            return "";
        } catch {
            
            return "";
        }
    };

    const handleChange = (key, value) => {
        setValues(prev => ({ ...prev, [key]: value }));
        setErrors(prev => ({ ...prev, [key]: undefined }));
    };

    // ── Validate required fields (non-auto) ──
    const validate = () => {
        const newErrors = {};

        if (required_all === false) return true;

        schema.forEach(field => {
            if (field.auto) return;
            const val = values[field.key];
            if (val === "" || val === null || val === undefined) {
                newErrors[field.key] = `${field.label} is required`;
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        onSubmit?.({ ...values });
    };

    if (!schema || !feature) return null;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {schema.map(field => (
                <DynamicField
                    key={field.key}
                    field={field}
                    value={values[field.key] ?? ""}
                    error={errors[field.key]}
                    onChange={handleChange}
                />
            ))}

            <Divider />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button onClick={onCancel} size="small" color="inherit">
                    Cancel
                </Button>
                <Button onClick={handleSubmit} size="small" variant="contained">
                    Save Feature
                </Button>
            </Box>
        </Box>
    );
}

// ── Renders the correct input based on the field descriptor ──
function DynamicField({ field, value, error, onChange }) {
    const { key, label, placeholder, data_type, auto, options } = field;

    // Auto — read only
    if (auto) {
        return (
            <TextField
                label={label}
                value={value}
                size="small"
                fullWidth
                slotProps={{
                    input: {
                        readOnly: true
                    }
                }}
                helperText="Auto-filled from geometry"
                sx={{ "& .MuiInputBase-input": { color: "text.secondary" } }}
            />
        );
    }

    // Dropdown — field has options
    if (options?.length > 0) {
        return (
            <FormControl fullWidth size="small" error={!!error}>
                <InputLabel id="label1" >{label}</InputLabel>
                <Select
                    labelId="label1"
                    value={value}
                    label={label}
                    onChange={(e) => onChange(key, e.target.value)}
                    MenuProps={{ style: { zIndex: 3500 } }}
                >
                    {options.map(opt => (
                        <MenuItem key={opt.id} value={opt.value}>
                            {opt.value}
                        </MenuItem>
                    ))}
                </Select>
                {error && <FormHelperText>{error}</FormHelperText>}
            </FormControl>
        );
    }

    // Number
    if (data_type === "float" || data_type === "int" || data_type === "integer") {
        return (
            <TextField
                label={label}
                value={value}
                onChange={(e) => onChange(key, e.target.value === "" ? "" : Number(e.target.value))}
                placeholder={placeholder}
                size="small"
                fullWidth
                type="number"
                error={!!error}
                helperText={error}
            />
        );
    }

    // Default — string / text
    return (
        <TextField
            label={label}
            value={value}
            onChange={(e) => onChange(key, e.target.value)}
            placeholder={placeholder}
            size="small"
            fullWidth
            error={!!error}
            helperText={error}
        />
    );
}